import { FormControl, FormHelperText, TextField } from "@mui/material";
import { ChangeEvent, FC } from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  IInput,
  IMuiFormControl,
  ValidationHelpers,
  valueAsNumber,
} from "@ryde/shared";

// name will be relative to the control of form passed to the input
// can be used to create independent nested forms
export const NumberInputByControl: FC<IMuiFormControl & IInput> = ({
  control,
  name,
  formLabel,
  formAttributes,
  formValidation,
  e2eData = "number-input",
  size = "medium",
  sx,
}) => {
  return name ? (
    <Controller
      name={name}
      control={control}
      rules={{ required: ValidationHelpers.hasRequiredRule(formValidation) }}
      render={({
        field: { onChange, ...restFieldProps },
        fieldState: { error },
      }) => {
        const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
          const newFieldValue = valueAsNumber(event.target.value);
          let nullOrNumber = newFieldValue === "" ? null : newFieldValue;

          if (nullOrNumber) {
            if (typeof formAttributes?.numberInput?.minValue === "number") {
              nullOrNumber = Math.max(
                formAttributes?.numberInput?.minValue,
                nullOrNumber,
              );
            }

            if (typeof formAttributes?.numberInput?.maxValue === "number") {
              nullOrNumber = Math.min(
                formAttributes?.numberInput?.maxValue,
                nullOrNumber,
              );
            }
          }

          onChange(nullOrNumber);
        };

        return (
          <FormControl error={!!error}>
            <TextField
              {...restFieldProps}
              onChange={handleChange}
              type={"number"}
              label={formLabel}
              required={ValidationHelpers.hasRequiredRule(formValidation)}
              data-e2e={e2eData}
              data-e2e-field-name={name}
              sx={sx}
              size={size}
              error={!!error}
              fullWidth
            />
            {/*TODO: add translation solution*/}
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  ) : null;
};

// name will be relative to the context of form set with formProvider
export const NumberInputByContext: FC<Omit<IInput, "control">> = ({
  name,
  formLabel,
  formValidation,
}) => {
  const { control, getValues } = useFormContext();

  const defaultValue = getValues(name as string);

  return name ? (
    <Controller
      name={name}
      control={control}
      rules={{ required: ValidationHelpers.hasRequiredRule(formValidation) }}
      defaultValue={defaultValue || ""}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error}>
          <TextField
            {...field}
            type={"number"}
            label={formLabel}
            required={ValidationHelpers.hasRequiredRule(formValidation)}
            error={!!error}
            fullWidth
          />
          {/*TODO: add translation solution*/}
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  ) : null;
};
